/**
 * Speech-to-Text service - uses OpenAI Whisper via the server.
 *
 * Records raw audio with MediaRecorder + adaptive VAD (Voice Activity Detection).
 * VAD calibrates against ambient noise briefly, then auto-stops the recording
 * once silence follows speech. Audio is sent to Whisper for transcription.
 */
import { webchatApi } from './api';
import { store } from '../state/store';

export interface SttOptions {
  continuous?: boolean;
  silenceMs?: number;
  calibrationMs?: number;
  flushIntervalMs?: number;
}

export interface SttCallbacks {
  onInterim: (text: string) => void;
  onFinal: (text: string) => void;
  onError: (errorKey: string) => void;
  onEnd: () => void;
  /** Normalized audio activity level in the 0..1 range for live recording visuals. */
  onAudioLevel?: (level: number) => void;
  onSpeechDetected?: () => void;
  /** Called when Whisper receives audio but detects no real speech (noise-only recording). */
  onNoise?: () => void;
  /**
   * Called once per recording session when gender is estimated from the voice's spectral centroid.
   * Based on 300–3400 Hz weighted centroid: below ~950 Hz → male, above → female.
   * Fires after ~1 s of qualified speech; may not fire if speech is too short.
   */
  onGenderDetected?: (gender: 'male' | 'female') => void;
  /**
   * Returns the accumulated transcript from previous segments in this turn.
   * Passed to Whisper as context so it can continue a long sentence naturally
   * instead of transcribing each chunk in isolation.
   */
  getContext?: () => string;
}

export interface SttService {
  isSupported(): boolean;
  start(lang: string, callbacks: SttCallbacks, options?: SttOptions): void;
  stop(): void;
  abort(): void;
  isListening(): boolean;
  /** True while audio is actively being recorded (false during Whisper round-trip). */
  isRecording(): boolean;
}

type SttBackendError = Error & {
  status?: number;
  code?: number;
  responseMessage?: string;
};

function resolveSttErrorKey(error: unknown): string {
  const sttError = error as SttBackendError | null;
  const message = sttError?.responseMessage || sttError?.message || '';
  const isMissingApiKey = (sttError?.status === 502 || sttError?.code === 502)
    && message.includes('No OpenAI API key configured for STT');

  return isMissingApiKey ? 'sttApiKeyMissing' : 'sttNetworkError';
}

// ─── Adaptive VAD ────────────────────────────────────────────────────────────

const DEFAULT_VAD_SILENCE_MS = 1000;
const CONTINUOUS_VAD_SILENCE_MS = 850;
const DEFAULT_VAD_CALIBRATION_MS = 300;
const CONTINUOUS_VAD_CALIBRATION_MS = 220;
const DEFAULT_RECORDER_FLUSH_INTERVAL_MS = 250;
const CONTINUOUS_RECORDER_FLUSH_INTERVAL_MS = 200;

interface VadDetectionState {
  hasSpeech: boolean;
  silenceStart: number | null;
  speechFrames: number;
  qualifiedSpeech: boolean;
  postSpeechNoiseFrames: number;
}

interface VadDetectionStepResult {
  nextState: VadDetectionState;
  qualifiedSpeechTriggered: boolean;
  shouldStopForSilence: boolean;
  speechActivityDetected: boolean;
}

function normalizeAudioLevel(rms: number): number {
  const normalized = Math.min(Math.max((rms - 8) / 48, 0), 1);
  // Smoothstep keeps quiet speech visible without making louder speech jumpy.
  return normalized * normalized * (3 - 2 * normalized);
}

export function advanceVadDetectionState(
  state: VadDetectionState,
  isAboveThreshold: boolean,
  now: number,
  silenceMs: number,
  minQualifiedSpeechFrames: number,
  minPostSpeechResumeFrames: number,
): VadDetectionStepResult {
  if (isAboveThreshold) {
    if (state.qualifiedSpeech && state.silenceStart !== null) {
      const nextNoiseFrames = state.postSpeechNoiseFrames + 1;
      const shouldResumeSpeech = nextNoiseFrames >= minPostSpeechResumeFrames;
      return {
        nextState: {
          hasSpeech: true,
          silenceStart: shouldResumeSpeech ? null : state.silenceStart,
          speechFrames: state.speechFrames,
          qualifiedSpeech: true,
          postSpeechNoiseFrames: shouldResumeSpeech ? 0 : nextNoiseFrames,
        },
        qualifiedSpeechTriggered: false,
        shouldStopForSilence: false,
        speechActivityDetected: true,
      };
    }

    const nextSpeechFrames = state.hasSpeech ? state.speechFrames + 1 : 1;
    const qualifiedSpeechTriggered =
      !state.qualifiedSpeech && nextSpeechFrames >= minQualifiedSpeechFrames;

    return {
      nextState: {
        hasSpeech: true,
        silenceStart: null,
        speechFrames: nextSpeechFrames,
        qualifiedSpeech: state.qualifiedSpeech || qualifiedSpeechTriggered,
        postSpeechNoiseFrames: 0,
      },
      qualifiedSpeechTriggered,
      shouldStopForSilence: false,
      speechActivityDetected: true,
    };
  }

  if (!state.hasSpeech) {
    return {
      nextState: {
        hasSpeech: false,
        silenceStart: null,
        speechFrames: 0,
        qualifiedSpeech: false,
        postSpeechNoiseFrames: 0,
      },
      qualifiedSpeechTriggered: false,
      shouldStopForSilence: false,
      speechActivityDetected: false,
    };
  }

  const nextSilenceStart = state.silenceStart ?? now;
  const shouldStopForSilence = now - nextSilenceStart > silenceMs;

  return {
    nextState: {
      hasSpeech: true,
      silenceStart: nextSilenceStart,
      // Require consecutive above-threshold frames before first qualification.
      speechFrames: state.qualifiedSpeech ? state.speechFrames : 0,
      qualifiedSpeech: state.qualifiedSpeech,
      postSpeechNoiseFrames: 0,
    },
    qualifiedSpeechTriggered: false,
    shouldStopForSilence,
    speechActivityDetected: false,
  };
}

/**
 * Attaches an adaptive VAD to a MediaStream.
 * - Calibrates ambient noise for the first calibrationMs milliseconds.
 * - Speech threshold = max(ambient_rms * 2.5, MIN_THRESHOLD).
 * - onSilence fires after silenceMs of continuous silence following speech.
 * Returns a cleanup function.
 */
/**
 * Estimate voice gender from the spectral centroid in the 300–3400 Hz speech band.
 * The `buf` array must already be filled by `analyser.getByteFrequencyData(buf)`.
 * Returns null when there is not enough energy to make a determination.
 *
 * Male voices have dominant harmonics in the lower portion of the speech band
 * (fundamental F0 ≈ 85–165 Hz, strong harmonics at 300–700 Hz), yielding a lower centroid.
 * Female voices have F0 ≈ 165–255 Hz with harmonics distributed higher, yielding a higher centroid.
 * Empirical threshold: ~950 Hz separates the distributions reliably for most speakers.
 */
function computeSpectralCentroid(
  buf: Uint8Array,
  speechLowBin: number,
  speechHighBin: number,
  binHz: number,
): number | null {
  let weightedSum = 0;
  let energySum = 0;
  for (let i = speechLowBin; i <= speechHighBin; i++) {
    weightedSum += i * binHz * buf[i];
    energySum += buf[i];
  }
  // Require a minimum energy level to avoid classifying noise.
  // energySum is a sum of Uint8 magnitudes over ~33 bins; threshold of 100 requires
  // an average of ~3/bin - enough to distinguish real speech from background noise.
  return energySum >= 100 ? weightedSum / energySum : null;
}

// Empirical threshold for the 300–3400 Hz band.
// Male formants keep the centroid lower; female formants are distributed higher.
// 1050 Hz was too low (everyone → female); 1500 Hz was too high (everyone → male).
// 1250 Hz splits the difference and matches typical spectral centroid distributions.
const GENDER_CENTROID_THRESHOLD_HZ = 1250;
const MIN_GENDER_SPEECH_FRAMES = 55; // ≈ 0.9 s at ~60 fps - enough for a reliable estimate

function createVad(
  stream: MediaStream,
  onSilence: () => void,
  onSpeechActivity: () => void,
  onQualifiedSpeech: () => void,
  onAudioLevel: ((level: number) => void) | undefined,
  onGenderDetected: ((gender: 'male' | 'female') => void) | undefined,
  silenceMs = DEFAULT_VAD_SILENCE_MS,
  calibrationMs = DEFAULT_VAD_CALIBRATION_MS,
): () => void {
  const MIN_THRESHOLD = 15;   // floor so a dead-quiet room doesn't need tiny levels
  const MAX_THRESHOLD = 55;   // capped lower so noisy environments (car/road) don't push threshold above voice level
  const CALIBRATION_SPEECH_THRESHOLD = 28;
  const MIN_CALIBRATION_SPEECH_FRAMES = 3;
  // Require ~333 ms of sustained signal above threshold at ~60 fps before treating it as speech.
  // 15 frames (~250 ms) was too easy for sustained road/engine noise to pass.
  const MIN_QUALIFIED_SPEECH_FRAMES = 20;
  // Once real speech already happened, require a brief sustained return before
  // cancelling the silence timeout. This ignores isolated road-noise spikes.
  const MIN_POST_SPEECH_RESUME_FRAMES = 5;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ACtx: typeof AudioContext = window.AudioContext ?? (window as any).webkitAudioContext;
  if (!ACtx) {
    // No Web Audio - fall back: fire onSilence after silenceMs unconditionally
    const t = setTimeout(onSilence, silenceMs);
    return () => clearTimeout(t);
  }

  const ctx = new ACtx();
  const src = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.4;
  src.connect(analyser);

  const buf = new Uint8Array(analyser.frequencyBinCount);

  // Human voice sits in 300–3400 Hz. Only measure energy in that band so that
  // low-frequency engine/road noise and high-frequency hiss don't trigger the VAD.
  // Both calibration and detection use the same band so thresholds stay consistent.
  const binHz = ctx.sampleRate / analyser.fftSize;
  const speechLowBin  = Math.max(0,              Math.round(300  / binHz));
  const speechHighBin = Math.min(buf.length - 1, Math.round(3400 / binHz));

  const getRms = (): number => {
    analyser.getByteFrequencyData(buf);
    let sum = 0;
    for (let i = speechLowBin; i <= speechHighBin; i += 1) sum += buf[i] * buf[i];
    return Math.sqrt(sum / (speechHighBin - speechLowBin + 1));
  };

  let done = false;
  let rafId = 0;

  // ── Phase 1: calibrate ambient noise ──────────────────────────────────────
  const calibrationStart = Date.now();
  let ambientPeak = 0;
  let ambientTotal = 0;
  let ambientSamples = 0;
  let speechFramesDuringCalibration = 0;

  const calibrate = () => {
    if (done) return;
    const rms = getRms();
    onAudioLevel?.(normalizeAudioLevel(rms));
    if (rms > ambientPeak) ambientPeak = rms;
    ambientTotal += rms;
    ambientSamples += 1;
    if (rms >= CALIBRATION_SPEECH_THRESHOLD) {
      speechFramesDuringCalibration += 1;
      onSpeechActivity();
    }

    if (Date.now() - calibrationStart < calibrationMs) {
      rafId = requestAnimationFrame(calibrate);
    } else {
      // Calibration done - enter detection phase
      const ambientMean = ambientSamples > 0 ? ambientTotal / ambientSamples : 0;
      const threshold = Math.min(
        Math.max(Math.max(ambientMean * 1.8, ambientPeak * 1.3), MIN_THRESHOLD),
        MAX_THRESHOLD,
      );
      const hasSpeech = speechFramesDuringCalibration >= MIN_CALIBRATION_SPEECH_FRAMES;
      // Do NOT carry calibration frames into detection or fire onQualifiedSpeech here.
      // Calibration frames use a fixed low threshold (22) while detection uses the
      // dynamically-calibrated threshold. Mixing them allows ambient noise to set
      // vadSpeechDetected = true, causing Whisper to receive silence and hallucinate.
      rafId = requestAnimationFrame(() => detect(
        threshold,
        hasSpeech,
        null,
        0,
        false,
        0,
      ));
    }
  };

  // ── Phase 2: detect speech then silence ───────────────────────────────────
  // Gender-detection accumulators (closure state, reset per VAD instance)
  let genderCentroidSum = 0;
  let genderCentroidCount = 0;
  let genderReported = false;

  const detect = (
    threshold: number,
    hasSpeech: boolean,
    silenceStart: number | null,
    speechFrames: number,
    qualifiedSpeech: boolean,
    postSpeechNoiseFrames: number,
  ) => {
    if (done) return;
    const rms = getRms();
    onAudioLevel?.(normalizeAudioLevel(rms));
    const now = Date.now();
    const step = advanceVadDetectionState(
      {
        hasSpeech,
        silenceStart,
        speechFrames,
        qualifiedSpeech,
        postSpeechNoiseFrames,
      },
      rms > threshold,
      now,
      silenceMs,
      MIN_QUALIFIED_SPEECH_FRAMES,
      MIN_POST_SPEECH_RESUME_FRAMES,
    );

    if (step.speechActivityDetected) {
      onSpeechActivity();
    }

    if (step.qualifiedSpeechTriggered) {
      onQualifiedSpeech();
    }

    ({
      hasSpeech,
      silenceStart,
      speechFrames,
      qualifiedSpeech,
      postSpeechNoiseFrames,
    } = step.nextState);

    if (step.shouldStopForSilence) {
      done = true;
      cancelAnimationFrame(rafId);
      ctx.close().catch(() => {});
      onSilence();
      return;
    }

    // Accumulate spectral centroid during qualified speech frames for gender detection.
    // buf is already populated by getRms() above so no extra FFT call is needed.
    if (qualifiedSpeech && rms > threshold && !genderReported && onGenderDetected) {
      const centroid = computeSpectralCentroid(buf, speechLowBin, speechHighBin, binHz);
      if (centroid !== null) {
        genderCentroidSum += centroid;
        genderCentroidCount++;
        if (genderCentroidCount >= MIN_GENDER_SPEECH_FRAMES) {
          genderReported = true;
          const avgCentroid = genderCentroidSum / genderCentroidCount;
          onGenderDetected(avgCentroid < GENDER_CENTROID_THRESHOLD_HZ ? 'male' : 'female');
        }
      }
    }

    rafId = requestAnimationFrame(() => detect(
      threshold,
      hasSpeech,
      silenceStart,
      speechFrames,
      qualifiedSpeech,
      postSpeechNoiseFrames,
    ));
  };

  rafId = requestAnimationFrame(calibrate);

  return () => {
    done = true;
    cancelAnimationFrame(rafId);
    ctx.close().catch(() => {});
  };
}

// ─── Whisper STT service ──────────────────────────────────────────────────────

const MAX_RECORDING_MS = 20_000; // hard stop after 20 s

class WhisperSttService implements SttService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private _isListening = false;
  private aborted = false;
  private callbacks: SttCallbacks | null = null;
  private locale = 'he';
  private continuous = false;
  private stopVad: (() => void) | null = null;
  private maxTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceMs = DEFAULT_VAD_SILENCE_MS;
  private calibrationMs = DEFAULT_VAD_CALIBRATION_MS;
  private flushIntervalMs = DEFAULT_RECORDER_FLUSH_INTERVAL_MS;
  /** True when VAD confirmed real speech - gates whether audio is sent to Whisper. */
  private vadSpeechDetected = false;
  private vadAnySpeechDetected = false;
  /** True when the user explicitly clicked stop - always send in that case. */
  private manualStop = false;

  isSupported(): boolean {
    return !!navigator.mediaDevices && 'MediaRecorder' in window;
  }

  start(locale: string, callbacks: SttCallbacks, options?: SttOptions): void {
    if (!this.isSupported()) {
      callbacks.onError('sttNotSupported');
      return;
    }
    this.abort();
    this.locale = locale;
    this.callbacks = callbacks;
    this.continuous = options?.continuous ?? false;
    this.silenceMs = options?.silenceMs
      ?? (this.continuous ? CONTINUOUS_VAD_SILENCE_MS : DEFAULT_VAD_SILENCE_MS);
    this.calibrationMs = options?.calibrationMs
      ?? (this.continuous ? CONTINUOUS_VAD_CALIBRATION_MS : DEFAULT_VAD_CALIBRATION_MS);
    this.flushIntervalMs = options?.flushIntervalMs
      ?? (this.continuous ? CONTINUOUS_RECORDER_FLUSH_INTERVAL_MS : DEFAULT_RECORDER_FLUSH_INTERVAL_MS);
    this._isListening = true;
    this.aborted = false;
    this.vadSpeechDetected = false;
    this.vadAnySpeechDetected = false;
    this.manualStop = false;
    this.beginRecording();
  }

  private finishAfterNoise(): void {
    this._isListening = false;
    if (this.callbacks?.onNoise) {
      this.callbacks.onNoise();
      return;
    }
    this.callbacks?.onEnd();
  }

  private beginRecording(): void {
    this.audioChunks = [];

    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      if (this.aborted) { stream.getTracks().forEach(t => t.stop()); return; }

      const mr = new MediaRecorder(stream);
      this.mediaRecorder = mr;
      let stopRequested = false;

      const stopRecording = () => {
        if (stopRequested || mr.state !== 'recording') return;
        stopRequested = true;
        try {
          mr.requestData();
        } catch {
          // Some browsers can throw if the recorder is already stopping.
        }
        mr.stop();
      };

      // Adaptive VAD: stops recording when silence follows speech.
      // onSilence is only called by VAD after real speech was detected - set the flag here.
      this.stopVad = createVad(
        stream,
        stopRecording,
        () => { this.vadAnySpeechDetected = true; },
        () => {
          this.vadSpeechDetected = true;
          this.callbacks?.onSpeechDetected?.();
        },
        this.callbacks?.onAudioLevel
          ? (level) => { this.callbacks?.onAudioLevel?.(level); }
          : undefined,
        this.callbacks?.onGenderDetected
          ? (gender) => { this.callbacks?.onGenderDetected?.(gender); }
          : undefined,
        this.silenceMs,
        this.calibrationMs,
      );

      // Hard-stop fallback - does NOT set vadSpeechDetected, so silent recordings are discarded.
      this.maxTimer = setTimeout(stopRecording, MAX_RECORDING_MS);

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };

      mr.onstop = () => {
        this.stopVad?.();
        this.stopVad = null;
        if (this.maxTimer) { clearTimeout(this.maxTimer); this.maxTimer = null; }
        if (this.mediaRecorder === mr) this.mediaRecorder = null;
        stream.getTracks().forEach(t => t.stop());

        if (this.aborted) return;

        // Only send to Whisper if VAD confirmed real speech, or the user
        // explicitly clicked stop (manualStop). Otherwise it's ambient noise
        // that would cause Whisper hallucinations like "כתוביות".
        const shouldSend = this.vadSpeechDetected || (this.manualStop && this.vadAnySpeechDetected);
        if (this.audioChunks.length === 0 || !shouldSend) {
          this.vadSpeechDetected = false;
          this.vadAnySpeechDetected = false;
          this.finishOrRestart();
          return;
        }

        this.vadSpeechDetected = false;
        this.vadAnySpeechDetected = false;
        // Use the actual MIME type the browser chose (e.g. audio/ogg;codecs=opus on Firefox,
        // audio/mp4 on Safari). Hardcoding audio/webm causes a format mismatch that makes
        // Whisper reject the file as "corrupted or unsupported".
        const actualMime = mr.mimeType || 'audio/webm';
        const blob = new Blob(this.audioChunks, { type: actualMime });
        this.audioChunks = [];

        const context = this.callbacks?.getContext?.() ?? '';
        webchatApi.transcribeAudio(
          blob,
          this.locale,
          context,
          store.getState().conversationId || undefined,
        )
          .then(({ transcript }) => {
            if (transcript === '__NOISE__' || !transcript.trim()) {
              // Whisper received audio but found no real speech, so end this
              // session instead of restarting and waiting on more noise.
              this.finishAfterNoise();
              return;
            }
            this.callbacks?.onFinal(transcript);
            this.finishOrRestart();
          })
          .catch((error: unknown) => {
            this._isListening = false;
            this.callbacks?.onError(resolveSttErrorKey(error));
          });
      };

      mr.start(this.flushIntervalMs);
    }).catch(() => {
      this._isListening = false;
      this.callbacks?.onError('sttPermissionDenied');
    });
  }

  private finishOrRestart(): void {
    if (this.continuous && this._isListening && !this.aborted) {
      this.beginRecording();
    } else {
      this._isListening = false;
      this.callbacks?.onEnd();
    }
  }

  stop(): void {
    this._isListening = false;
    this.manualStop = true; // user explicitly stopped - send whatever was recorded
    this.stopVad?.(); this.stopVad = null;
    if (this.maxTimer) { clearTimeout(this.maxTimer); this.maxTimer = null; }
    try {
      if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.stop();
    } catch { /* ignore */ }
  }

  abort(): void {
    this.aborted = true;
    this._isListening = false;
    this.audioChunks = [];
    this.vadSpeechDetected = false;
    this.vadAnySpeechDetected = false;
    this.manualStop = false;
    this.stopVad?.(); this.stopVad = null;
    if (this.maxTimer) { clearTimeout(this.maxTimer); this.maxTimer = null; }
    try {
      if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.stop();
    } catch { /* ignore */ }
    this.mediaRecorder = null;
    this.callbacks = null;
  }

  isListening(): boolean {
    return this._isListening;
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

let instance: WhisperSttService | null = null;

export function getSttService(): SttService {
  if (!instance) {
    instance = new WhisperSttService();
  }
  return instance;
}
